import React, { useEffect, useRef, useState } from 'react';
import Header from '../../component/header/header';
import { FaPlus } from 'react-icons/fa';
import Button from '../../component/button/button';
import { useNavigate } from 'react-router-dom';
import { SubjectErrorBags, TeacherData } from './SubjectInterface';
import SubjectImageUpload from './SubjectImage';
import CustomInput from '../../component/CustomInput/CustomInput';
import Select from 'react-select';
import { listTeacher } from '../../api/teacher';
import { saveSubjectData } from '../../api/subject';
import {
  successMessage,
  errorMessageShow,
  LoaderComponent,
} from '../../utils/Helper';
import LevelLabelEditor from './detail/LevelLabelEditor';
import {
  DEFAULT_LEVEL_LABELS,
  validateLevelLabels,
  hasLabelErrors,
} from './detail/hierarchyUtils';
import axios from 'axios';

export default function CreateSubject() {
  const navigate = useNavigate();
  const [teacherData, setTeacherData] = useState<TeacherData[]>([]);
  const [apiLoading, setApiLoading] = useState(false);
  const [loading, setLoading] = useState(false);

  const [title, setTitle] = useState('');
  const [teachers, setTeachers] = useState<string[]>([]);
  const [image, setImage] = useState<any>('');
  const [useDefaultLabels, setUseDefaultLabels] = useState(true);
  const [labels, setLabels] = useState<string[]>([...DEFAULT_LEVEL_LABELS]);

  const [errorBags, setErrorBags] = useState<SubjectErrorBags>({});
  const [labelErrors, setLabelErrors] = useState<string[]>([]);
  const isSubmittingRef = useRef(false);

  const allowedImageTypes = ['image/jpeg', 'image/png', 'image/webp'];
  const maxSize = 3 * 1024 * 1024;

  useEffect(() => {
    const fetchTeacherData = async () => {
      try {
        setApiLoading(true);
        const response = await listTeacher(1, '', 100);
        const teachersList = response?.data?.data?.teachers || [];
        if (!teachersList.length) {
          errorMessageShow('Oops! No teacher found.');
        }
        setTeacherData(teachersList);
      } catch (error) {
        console.error('Error fetching teacher data:', error);
      } finally {
        setApiLoading(false);
      }
    };
    fetchTeacherData();
  }, []);

  const handleNavigateBack = () => navigate(-1);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files ? e.target.files[0] : null;
    setImage(file);
  };

  const handleSubjectSubmit = () => {
    if (isSubmittingRef.current || loading || apiLoading) return;

    const effectiveLabels = useDefaultLabels ? [...DEFAULT_LEVEL_LABELS] : labels;
    const nextLabelErrors = useDefaultLabels
      ? []
      : validateLevelLabels(labels);
    const nextErrors: SubjectErrorBags = {};
    let hasError = false;

    if (title.trim().length < 3) {
      nextErrors.title = 'Title must be at least 3 characters.';
      hasError = true;
    }
    if (teachers.length === 0) {
      nextErrors.teachers = 'Please select at least one teacher.';
      hasError = true;
    }
    if (image && !allowedImageTypes.includes(image.type)) {
      nextErrors.image = 'Invalid image format. Only JPG, PNG, and WebP allowed.';
      hasError = true;
    }
    if (image && image.size > maxSize) {
      nextErrors.image = 'Image size should not exceed 3MB.';
      hasError = true;
    }
    if (hasLabelErrors(nextLabelErrors)) {
      setLabelErrors(nextLabelErrors);
      hasError = true;
    }

    if (hasError) {
      setErrorBags(nextErrors);
      errorMessageShow('Please fix the form errors before submitting.');
      return;
    }

    try {
      isSubmittingRef.current = true;
      setLoading(true);
      const formData = new FormData();
      formData.append('title', title.trim());
      teachers.forEach(teacher => formData.append('teachers', teacher));
      effectiveLabels.forEach(label =>
        formData.append('level_labels', label.trim())
      );
      if (image) {
        formData.append('image', image);
      }

      saveSubjectData(formData)
        .then(response => {
          successMessage(`Subject "${title}" created successfully!`);
          const newId = response?.data?.data?.id;
          navigate(newId ? `/subjects/${newId}` : '/subjects');
        })
        .catch(error => {
          errorMessageShow(error.response?.data?.message);
          console.error('Error creating subject:', error);
        })
        .finally(() => {
          isSubmittingRef.current = false;
          setLoading(false);
        });
    } catch (error: unknown) {
      if (axios.isAxiosError(error)) {
        errorMessageShow(
          error.response?.data?.message || 'An unexpected error occurred.'
        );
      }
      isSubmittingRef.current = false;
      setLoading(false);
    }
  };

  const selectedTeachers = teachers
    .map(id => {
      const teacher = teacherData.find(t => t.id === id);
      return teacher
        ? { value: teacher.id, label: `${teacher.first_name} ${teacher.last_name}` }
        : null;
    })
    .filter((o): o is { value: string; label: string } => o !== null);

  return (
    <div className="content">
      <Header
        pageTitle="Add Subject"
        buttonText={loading ? 'Creating …' : 'Add Subject'}
        buttonIcon={<FaPlus />}
        extraButton={<Button text="Close" onClick={handleNavigateBack} />}
        onClick={() => handleSubjectSubmit()}
        isActive={!apiLoading && !loading}
      />

      <div className="mt-4 shadow-md">
        <form className="bg-white p-6 rounded subject_overview_panel">
          <div className="subject_overview_main">
            <div className="subject_overview_image">
              <SubjectImageUpload
                inputChangeHandler={handleFileChange}
                setFormData={() => {}}
                setErrorBags={setErrorBags}
              />
              {errorBags.image && (
                <p className="text-red-500 text-xs mt-1">{errorBags.image}</p>
              )}
            </div>

            <div className="subject_overview_fields">
              <div className="form-group mb-3">
                <label className="text-sm">
                  Subject Title <span className="text-red-500">*</span>
                </label>
                <CustomInput
                  placeholder="Subject title"
                  value={title}
                  name="title"
                  onChange={e => {
                    setTitle(e.target.value);
                    setErrorBags(prev => ({ ...prev, title: '' }));
                  }}
                  error={errorBags.title}
                  style={{ height: 42 }}
                />
              </div>

              <div className="form-group mb-3">
                <label className="text-sm">
                  Assign Teachers <span className="text-red-500">*</span>
                </label>
                {apiLoading ? (
                  <LoaderComponent size="sm" label="Loading teachers…" />
                ) : (
                  <Select
                    isMulti
                    value={selectedTeachers}
                    options={teacherData.map(teacher => ({
                      value: teacher.id,
                      label: `${teacher.first_name} ${teacher.last_name}`,
                    }))}
                    onChange={selected => {
                      setTeachers(
                        (selected as readonly { value: string }[]).map(
                          o => o.value
                        )
                      );
                      setErrorBags(prev => ({ ...prev, teachers: '' }));
                    }}
                    placeholder="Select teacher(s)"
                  />
                )}
                {errorBags.teachers && (
                  <p className="text-red-500 text-xs mt-1">
                    {errorBags.teachers}
                  </p>
                )}
              </div>
            </div>
          </div>

          <div className="level_label_default_toggle">
            <label className="level_label_checkbox">
              <input
                type="checkbox"
                checked={useDefaultLabels}
                onChange={e => {
                  const checked = e.target.checked;
                  setUseDefaultLabels(checked);
                  setLabels(
                    checked ? [...DEFAULT_LEVEL_LABELS] : ['', '', '', '']
                  );
                  setLabelErrors([]);
                }}
              />
              <span>Use default level names (Topic, Subtopic, Chapter, Section)</span>
            </label>
          </div>

          {!useDefaultLabels && (
            <LevelLabelEditor
              labels={labels}
              errors={labelErrors}
              onChange={next => {
                setLabels(next);
                setLabelErrors([]);
              }}
            />
          )}

          <p className="text-sm text-gray-500">
            You can add and organize items for each level after creating the
            subject.
          </p>

          <div className="subject_overview_actions">
            <Button
              text={loading ? 'Creating …' : 'Add Subject'}
              buttonType="button"
              onClick={() => handleSubjectSubmit()}
              disabled={loading || apiLoading}
              isLoading={loading}
              loadingText="Creating …"
              style={{ fontSize: 14, height: 44 }}
            />
          </div>
        </form>
      </div>
    </div>
  );
}
